/**
 * Referral Claim Hook
 *
 * Provides non-blocking referral token claiming functionality for patient authentication.
 * Handles token validation, cleanup, and error management with silent operation.
 *
 * @module hooks/useReferralClaim
 */

import { useState, useCallback } from 'react';
import { referralDomain, ReferralApiError, type ClaimReferralResponse } from '@/services/domains/ReferralDomain';
import type {
  ReferralClaimResponse,
  ReferralClaimError,
  UseReferralClaimReturn
} from '@/types/referral';
import { getLogger } from '@/services/shared/LoggerFactory';

/**
 * Transform ClaimReferralResponse from domain to hook response format
 */
const transformClaimResponse = (domainResponse: ClaimReferralResponse): ReferralClaimResponse | null => {
  if (domainResponse.status !== 'OK' || !domainResponse.referral) {
    return null;
  }

  const referral = domainResponse.referral;
  return {
    id: referral.id,
    referral_token: referral.referral_token,
    referring_physician_id: referral.referring_physician_id,
    dietitian_id: '', // Not available in domain response, will need to be handled
    patient_name: referral.patient_name || '',
    date_of_birth: '', // Not available in domain response
    patient_id_number: '', // Not available in domain response
    patient_id_type: '', // Not available in domain response
    phone: '', // Not available in domain response
    email: referral.email || '',
    conditions: [], // Not available in domain response
    additional_notes: referral.additional_notes || '',
    status: referral.status,
    claimed_by_user_id: referral.claimed_by_user_id || '',
    claimed_at: referral.claimed_at || new Date().toISOString(),
    expires_at: referral.expires_at,
    created_at: referral.created_at,
    updated_at: referral.updated_at,
    referring_physician_name: referral.referring_physician_name,
    referring_physician_email: '', // Not available in domain response
    dietitian_name: '', // Not available in domain response
    dietitian_email: '', // Not available in domain response
    dietitian_scheduling_uri: '' // Not available in domain response
  };
};

/**
 * Transform ReferralApiError from domain to hook error format
 */
const transformClaimError = (domainError: ReferralApiError): ReferralClaimError => {
  // Map domain error codes to hook error codes
  let errorCode: ReferralClaimError['code'] = 'NETWORK_ERROR';

  if (domainError.code) {
    switch (domainError.code) {
      case 'INVALID_TOKEN':
        errorCode = 'INVALID_TOKEN';
        break;
      case 'ALREADY_CLAIMED':
        errorCode = 'ALREADY_CLAIMED';
        break;
      case 'EXPIRED':
        errorCode = 'EXPIRED';
        break;
      case 'NOT_FOUND':
        errorCode = 'NOT_FOUND';
        break;
      case 'UNAUTHORIZED':
        errorCode = 'UNAUTHORIZED';
        break;
      case 'FORBIDDEN':
        errorCode = 'FORBIDDEN';
        break;
      default:
        errorCode = 'NETWORK_ERROR';
    }
  } else if (domainError.status) {
    // Map HTTP status codes to error codes
    switch (domainError.status) {
      case 400:
        errorCode = 'INVALID_TOKEN';
        break;
      case 401:
        errorCode = 'UNAUTHORIZED';
        break;
      case 403:
        errorCode = 'FORBIDDEN';
        break;
      case 404:
        errorCode = 'NOT_FOUND';
        break;
      case 409:
        errorCode = 'ALREADY_CLAIMED';
        break;
      case 410:
        errorCode = 'EXPIRED';
        break;
      default:
        errorCode = 'NETWORK_ERROR';
    }
  }

  return {
    message: domainError.message,
    code: errorCode,
    details: {
      originalError: domainError,
      status: domainError.status,
      fieldErrors: domainError.fieldErrors
    }
  };
};

/**
 * Validate referral token format
 * Basic validation to ensure token is not empty and has reasonable format
 */
const validateToken = (token: string): boolean => {
  if (!token || typeof token !== 'string') {
    return false;
  }

  // Remove whitespace and check length
  const cleanToken = token.trim();
  if (cleanToken.length < 6) {
    return false;
  }

  // Basic alphanumeric check (allowing common special characters)
  const tokenRegex = /^[A-Za-z0-9\-_]+$/;
  return tokenRegex.test(cleanToken);
};

/**
 * Get referral token from localStorage using domain service
 */
const getStoredToken = (): string | null => {
  const logger = getLogger();
  try {
    return referralDomain.getStoredReferralToken();
  } catch (error) {
    logger.log('Failed to read referral token from localStorage:', error);
    return null;
  }
};

/**
 * Remove referral token from localStorage using domain service
 */
const clearStoredToken = (): void => {
  const logger = getLogger();
  try {
    const success = referralDomain.clearStoredReferralToken();
    if (!success) {
      logger.warn('Failed to clear referral token from localStorage');
    }
  } catch (error) {
    logger.warn('Failed to clear referral token from localStorage:', error);
  }
};

/**
 * Custom hook for referral token claiming
 *
 * Provides non-blocking referral claiming with comprehensive error handling.
 * Designed to be called after successful patient authentication.
 */
export function useReferralClaim(): UseReferralClaimReturn {
  const logger = getLogger();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ReferralClaimError | null>(null);

  /**
   * Clear any existing error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Claim a referral token
   *
   * @param token - Optional token to claim. If not provided, will use stored token
   * @returns Promise resolving to claimed referral data or null if failed
   */
  const claimReferral = useCallback(async (token?: string): Promise<ReferralClaimResponse | null> => {
    // Clear previous error state
    setError(null);
    setIsLoading(true);

    try {
      // Determine which token to use
      const tokenToUse = token || getStoredToken();

      if (!tokenToUse) {
        logger.log('No referral token available to claim');
        return null;
      }

      // Validate token format
      if (!validateToken(tokenToUse)) {
        const validationError: ReferralClaimError = {
          message: 'Invalid referral token format',
          code: 'INVALID_TOKEN'
        };
        setError(validationError);
        logger.warn('Invalid referral token format:', tokenToUse);

        // Clear invalid token from storage
        clearStoredToken();
        return null;
      }

      logger.log('Attempting to claim referral token...');

      // Attempt to claim the referral using the domain service
      const domainResponse = await referralDomain.claimReferral({ token: tokenToUse });

      // Transform the domain response to the hook response format
      const claimedReferral = transformClaimResponse(domainResponse);

      if (!claimedReferral) {
        // Handle unsuccessful claim response
        const claimError: ReferralClaimError = {
          message: 'Failed to claim referral - invalid response',
          code: 'NETWORK_ERROR'
        };
        setError(claimError);
        logger.warn('Referral claim failed - invalid response:', domainResponse);
        return null;
      }

      logger.log('Referral claimed successfully:', {
        referralId: claimedReferral.id,
        patientName: claimedReferral.patient_name,
        dietitianName: claimedReferral.dietitian_name
      });

      // Clear token from storage after successful claim
      clearStoredToken();

      return claimedReferral;

    } catch (error) {
      // Handle ReferralApiError from domain
      if (error && typeof error === 'object' && error instanceof ReferralApiError) {
        const claimError = transformClaimError(error);
        setError(claimError);

        // Log different error types with appropriate levels
        switch (claimError.code) {
          case 'INVALID_TOKEN':
          case 'NOT_FOUND':
            logger.warn('Referral claim failed:', claimError.message);
            // Clear invalid/not found tokens from storage
            clearStoredToken();
            break;
          case 'EXPIRED':
            logger.warn('Referral claim failed:', claimError.message);
            // Clear expired tokens from storage
            clearStoredToken();
            break;
          case 'ALREADY_CLAIMED':
            logger.log('Referral already claimed:', claimError.message);
            // Clear already claimed token from storage
            clearStoredToken();
            break;
          case 'UNAUTHORIZED':
            logger.error('Authorization error claiming referral:', claimError.message);
            // Don't clear token for auth errors
            break;
          case 'FORBIDDEN':
            logger.error('Authorization error claiming referral:', claimError.message);
            // Don't clear token for auth errors
            break;
          case 'NETWORK_ERROR':
            logger.error('Network error claiming referral:', claimError.message);
            // Don't clear token for network errors
            break;
          default:
            logger.error('Unknown error claiming referral:', claimError.message);
            // Don't clear token for unknown errors
        }
      } else if (error && typeof error === 'object' && 'code' in error) {
        // Handle legacy ReferralClaimError format for backward compatibility
        const claimError = error as ReferralClaimError;
        setError(claimError);
        logger.warn('Legacy error format detected:', claimError.message);
      } else {
        // Handle unexpected errors
        const unexpectedError: ReferralClaimError = {
          message: 'An unexpected error occurred while claiming referral',
          code: 'NETWORK_ERROR',
          details: { originalError: error }
        };
        setError(unexpectedError);
        logger.error('Unexpected error claiming referral:', error);
      }

      return null;

    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    claimReferral,
    isLoading,
    error,
    clearError
  };
}